import React from 'react';
import { SearchBar } from '../../molecules/SearchBar';
import { Button } from '../../atoms/Button';
import { Dropdown } from '../../atoms/Dropdown';
import type { CaseFilters, ProjectStatus } from '@lnr-v2/types';
import type { DropdownOption } from '../../atoms/Dropdown/Dropdown';
import styles from './FiltersPanel.module.css';

export interface FiltersPanelProps {
  filters: CaseFilters;
  onFiltersChange: (filters: Partial<CaseFilters>) => void;
  onClearFilters: () => void;
  categories?: Array<{ id: string; name: string }>;
  loading?: boolean;
  totalCount?: number;
}

const STATUS_OPTIONS: Array<{ value: ProjectStatus; label: string }> = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ARCHIVED', label: 'Archived' },
];

export const FiltersPanel: React.FC<FiltersPanelProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  categories = [],
  loading = false,
  totalCount,
}) => {
  const hasActiveFilters =
    filters.search ||
    filters.categoryId ||
    filters.status ||
    filters.isFeatured !== undefined;

  const handleSearchChange = (value: string) => {
    onFiltersChange({ search: value, page: 1 });
  };

  const handleCategoryChange = (value: string | string[]) => {
    const categoryId = typeof value === 'string' ? value : undefined;
    onFiltersChange({
      categoryId: categoryId || undefined,
      page: 1,
    });
  };

  const handleStatusChange = (value: string | string[]) => {
    const status =
      typeof value === 'string' ? (value as ProjectStatus) : undefined;
    onFiltersChange({
      status: status || undefined,
      page: 1,
    });
  };

  const handleFeaturedChange = (value: string | string[]) => {
    const featuredValue = typeof value === 'string' ? value : '';
    onFiltersChange({
      isFeatured: featuredValue === '' ? undefined : featuredValue === 'true',
      page: 1,
    });
  };

  // Quick filter handlers
  const handleQuickFilter = (
    filterType: 'featured' | 'inProgress' | 'recent'
  ) => {
    switch (filterType) {
      case 'featured':
        onFiltersChange({ isFeatured: true, page: 1 });
        break;
      case 'inProgress':
        onFiltersChange({ status: 'IN_PROGRESS', page: 1 });
        break;
      case 'recent':
        // Clear all filters to show most recent
        onClearFilters();
        break;
    }
  };

  // Convert categories to dropdown options
  const categoryOptions: DropdownOption[] = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  // Convert status to dropdown options
  const statusOptions: DropdownOption[] = STATUS_OPTIONS.map((status) => ({
    value: status.value,
    label: status.label,
  }));

  // Featured dropdown options
  const featuredOptions: DropdownOption[] = [
    { value: 'true', label: 'Featured Only' },
    { value: 'false', label: 'Not Featured' },
  ];

  return (
    <div className={styles.filtersPanel}>
      {/* Header with Results Counter */}
      <div className={styles.header}>
        <div className={styles.resultsCounter}>
          {loading ? (
            <span className={styles.resultsLoading}>Loading...</span>
          ) : (
            <>
              <span className={styles.resultsCount}>{totalCount ?? 0}</span>
              <span className={styles.resultsLabel}>
                {totalCount === 1 ? 'case' : 'cases'} found
              </span>
            </>
          )}
        </div>

        {/* Quick Filters */}
        <div className={styles.quickFilters}>
          <button
            className={`${styles.quickFilter} ${
              filters.isFeatured === true ? styles.quickFilterActive : ''
            }`}
            onClick={() => handleQuickFilter('featured')}
            disabled={loading}
          >
            Featured
          </button>
          <button
            className={`${styles.quickFilter} ${
              filters.status === 'IN_PROGRESS' ? styles.quickFilterActive : ''
            }`}
            onClick={() => handleQuickFilter('inProgress')}
            disabled={loading}
          >
            In Progress
          </button>
          <button
            className={`${styles.quickFilter} ${
              !hasActiveFilters ? styles.quickFilterActive : ''
            }`}
            onClick={() => handleQuickFilter('recent')}
            disabled={loading}
          >
            Recent
          </button>
        </div>
      </div>

      {/* Main Filters */}
      <div className={styles.mainFilters}>
        <SearchBar
          value={filters.search || ''}
          onChange={handleSearchChange}
          placeholder="Search cases by name, client, or description..."
          loading={loading}
          className={styles.searchBar}
        />

        <div className={styles.dropdownFilters}>
          <Dropdown
            options={categoryOptions}
            value={filters.categoryId || ''}
            onChange={handleCategoryChange}
            placeholder="All Categories"
            disabled={loading}
            searchable={true}
            clearable={true}
            className={styles.dropdown}
          />

          <Dropdown
            options={statusOptions}
            value={filters.status || ''}
            onChange={handleStatusChange}
            placeholder="All Statuses"
            disabled={loading}
            clearable={true}
            className={styles.dropdown}
          />

          <Dropdown
            options={featuredOptions}
            value={
              filters.isFeatured === undefined
                ? ''
                : filters.isFeatured
                  ? 'true'
                  : 'false'
            }
            onChange={handleFeaturedChange}
            placeholder="All Cases"
            disabled={loading}
            clearable={true}
            className={styles.dropdown}
          />
        </div>

        {hasActiveFilters && (
          <Button variant="ghost" size="md" onClick={onClearFilters}>
            ✕ Clear All Filters
          </Button>
        )}
      </div>

      {hasActiveFilters && (
        <div className={styles.activeFilters}>
          <span className={styles.activeFiltersLabel}>Active filters:</span>
          {filters.search && (
            <span className={styles.filterTag}>
              Search: &ldquo;{filters.search}&rdquo;
              <button
                onClick={() => onFiltersChange({ search: undefined, page: 1 })}
                className={styles.removeFilter}
                aria-label="Remove search filter"
              >
                ×
              </button>
            </span>
          )}
          {filters.categoryId && (
            <span className={styles.filterTag}>
              Category:{' '}
              {categories.find((c) => c.id === filters.categoryId)?.name ||
                'Unknown'}
              <button
                onClick={() =>
                  onFiltersChange({ categoryId: undefined, page: 1 })
                }
                className={styles.removeFilter}
                aria-label="Remove category filter"
              >
                ×
              </button>
            </span>
          )}
          {filters.status && (
            <span className={styles.filterTag}>
              Status:{' '}
              {STATUS_OPTIONS.find((s) => s.value === filters.status)?.label ||
                filters.status}
              <button
                onClick={() => onFiltersChange({ status: undefined, page: 1 })}
                className={styles.removeFilter}
                aria-label="Remove status filter"
              >
                ×
              </button>
            </span>
          )}
          {filters.isFeatured !== undefined && (
            <span className={styles.filterTag}>
              {filters.isFeatured ? 'Featured Only' : 'Not Featured'}
              <button
                onClick={() =>
                  onFiltersChange({ isFeatured: undefined, page: 1 })
                }
                className={styles.removeFilter}
                aria-label="Remove featured filter"
              >
                ×
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
